import { dataService } from '../services/dataService.js';
import { ratingService } from '../services/ratingService.js';
import { seoService } from '../services/seoService.js';
import { createRatingSheet } from '../components/RatingSheet.js';
import { createDiscussionBoard } from '../components/DiscussionBoard.js';
import { createWhereToWatch } from '../components/WhereToWatch.js';
import { createInterviewsList } from '../components/InterviewsList.js';
import { openStoryCardModal } from '../components/StoryCardModal.js';
import { Toast } from '../components/Toast.js';

export async function renderMovieDetailView(movieId) {
  const container = document.createElement('div');
  container.className = 'main-content';

  const movie = await dataService.getMovie(movieId);
  if (!movie) {
    container.innerHTML = `<div style="text-align: center; padding: var(--space-2xl) 0;"><p>Movie not found.</p><a href="#/" class="btn-primary" style="margin-top: 16px;">Back to Home</a></div>`;
    return container;
  }

  // Inject SEO metadata and JSON-LD
  seoService.update({
    title: `${movie.title} (${movie.year})`,
    description: movie.synopsis,
    image: movie.poster,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Movie',
      'name': movie.title,
      'alternateName': movie.sinhalaTitle,
      'datePublished': movie.releaseDate,
      'director': { '@type': 'Person', 'name': movie.director.name },
      'aggregateRating': {
        '@type': 'AggregateRating',
        'ratingValue': movie.rating,
        'ratingCount': movie.ratingCount,
        'bestRating': '5'
      }
    }
  });

  const userRating = ratingService.getUserRating(movie.id);

  container.innerHTML = `
    <!-- Cinematic Hero Header -->
    <div style="position: relative; border-radius: var(--radius-lg); overflow: hidden; margin-bottom: var(--space-xl); background: var(--bg-surface);">
      <div style="position: relative; width: 100%; height: 260px;">
        <img src="${movie.backdrop || movie.poster}" alt="${movie.title}" style="width: 100%; height: 100%; object-fit: cover; filter: brightness(0.6);" />
        <div style="position: absolute; inset: 0; background: linear-gradient(to top, var(--bg-surface) 5%, transparent 95%);"></div>
      </div>

      <div style="padding: 0 var(--space-md) var(--space-lg) var(--space-md); display: flex; flex-direction: column; gap: var(--space-md); margin-top: -60px; position: relative; z-index: 10;">
        <div style="display: flex; gap: var(--space-md); align-items: flex-end;">
          <img src="${movie.poster}" alt="${movie.title}" style="width: 110px; height: 160px; border-radius: var(--radius-md); object-fit: cover; box-shadow: var(--shadow-lg); border: 2px solid var(--border-subtle); flex-shrink: 0;" />
          <div style="flex: 1;">
            <div style="display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 6px;">
              ${(movie.genres || []).map(g => `<span class="badge-pill">${g}</span>`).join('')}
            </div>
            <h1 style="font-size: 1.6rem; font-weight: 800; line-height: 1.2;">${movie.title}</h1>
            <div style="font-size: 1rem; color: var(--accent-gold); font-weight: 600; margin-bottom: 4px;">${movie.sinhalaTitle || ''}</div>
            <div style="font-size: 0.82rem; color: var(--text-muted); display: flex; gap: 8px;">
              <span>${movie.year}</span> • <span>${movie.runtime}</span> • <span>Dir: ${movie.director.name}</span>
            </div>
          </div>
        </div>

        <!-- Rating & Actions Bar -->
        <div style="display: flex; gap: var(--space-sm); align-items: center; justify-content: space-between; background: var(--bg-surface-elevated); padding: 12px 16px; border-radius: var(--radius-md); border: 1px solid var(--border-subtle); flex-wrap: wrap;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 1.3rem; color: var(--accent-gold);">★</span>
            <div>
              <span id="display-rating" style="font-size: 1.1rem; font-weight: 800;">${movie.rating}</span>
              <span style="font-size: 0.75rem; color: var(--text-muted);">/ 5 (<span id="display-count">${movie.ratingCount}</span> votes)</span>
            </div>
          </div>
          <div style="display: flex; gap: var(--space-xs); flex-wrap: wrap;">
            <button id="rate-movie-btn" class="btn-primary clickable-item" style="padding: 8px 14px; font-size: 0.85rem;">
              ★ <span id="rate-btn-text">${userRating ? `Your: ${userRating}★` : 'Rate Movie'}</span>
            </button>
            <button id="share-story-btn" class="btn-secondary clickable-item" style="padding: 8px 14px; font-size: 0.85rem;">
              📸 Share Story
            </button>
            <button id="bookmark-movie-btn" class="btn-secondary clickable-item" style="padding: 8px 14px; font-size: 0.85rem;">
              🔖 Bookmark
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Synopsis -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header"><h2 class="section-title">Synopsis</h2></div>
      <p style="color: var(--text-secondary); line-height: 1.6; font-size: 0.95rem;">${movie.synopsis}</p>
    </section>

    <!-- Cast Carousel -->
    <section style="margin-bottom: var(--space-xl);">
      <div class="section-header"><h2 class="section-title">Cast & Characters</h2></div>
      <div class="horizontal-scroll" id="cast-list">
        ${(movie.cast || []).map(actor => `
          <a href="#/person/${actor.personId}" class="person-card clickable-item">
            <div class="person-avatar-wrap">
              <img src="${actor.avatar}" alt="${actor.name}" class="person-avatar" loading="lazy" />
            </div>
            <div class="person-name">${actor.name}</div>
            <div style="font-size: 0.72rem; color: var(--accent-gold);">${actor.sinhalaName || ''}</div>
            <div class="person-role">${actor.character}</div>
          </a>
        `).join('')}
      </div>
    </section>

    <!-- Soundtrack / Songs -->
    ${movie.songs && movie.songs.length > 0 ? `
      <section style="margin-bottom: var(--space-xl);">
        <div class="section-header"><h2 class="section-title">Soundtrack & Lyrics</h2></div>
        <div style="display: flex; flex-direction: column; gap: var(--space-xs);">
          ${movie.songs.map(song => `
            <a href="#/song/${song.id}" class="song-card clickable-item">
              <div class="song-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle></svg>
              </div>
              <div class="song-info">
                <div class="song-title">${song.title}</div>
                <div style="font-size: 0.78rem; color: var(--accent-gold);">${song.sinhalaTitle}</div>
                <div class="song-artists">${song.singers ? song.singers.join(', ') : ''}</div>
              </div>
              <span class="badge-pill badge-gold" style="font-size: 0.72rem;">View Lyrics</span>
            </a>
          `).join('')}
        </div>
      </section>
    ` : ''}

    <!-- Filming Locations -->
    ${movie.filmingLocations && movie.filmingLocations.length > 0 ? `
      <section style="margin-bottom: var(--space-xl);">
        <div class="section-header"><h2 class="section-title">Filming Locations</h2></div>
        <div style="display: flex; gap: var(--space-sm); flex-wrap: wrap;">
          ${movie.filmingLocations.map(loc => `
            <a href="#/location/${loc.id}" class="badge-pill clickable-item" style="padding: 10px 14px; font-size: 0.85rem; border-color: var(--border-gold);">
              📍 <strong>${loc.name}</strong> (${loc.scenes})
            </a>
          `).join('')}
        </div>
      </section>
    ` : ''}

    <!-- Conditional Dynamic Sections: Where to watch, quotes, trivia, awards, interviews -->
    <div id="dynamic-sections"></div>

    <!-- Cinema Club Discussion Placeholder -->
    <div id="discussion-placeholder"></div>
  `;

  // Attach Where to Watch
  const dynamicSections = container.querySelector('#dynamic-sections');
  const watchEl = createWhereToWatch(movie.whereToWatch);
  if (watchEl) dynamicSections.appendChild(watchEl);

  // Quotes (Conditional)
  if (movie.quotes && movie.quotes.length > 0) {
    const qSec = document.createElement('section');
    qSec.style.marginTop = 'var(--space-xl)';
    qSec.innerHTML = `
      <div class="section-header"><h2 class="section-title">Memorable Quotes</h2></div>
      ${movie.quotes.map(q => `
        <div style="background: var(--bg-surface); border-left: 3px solid var(--accent-gold); padding: 12px 16px; border-radius: var(--radius-sm); margin-bottom: 8px;">
          <p style="font-style: italic; color: var(--text-primary); font-size: 0.95rem;">"${q.quote}"</p>
          <span style="font-size: 0.8rem; color: var(--accent-gold); display: block; margin-top: 4px;">— ${q.speaker}</span>
        </div>
      `).join('')}
    `;
    dynamicSections.appendChild(qSec);
  }

  // Trivia (Conditional)
  if (movie.trivia && movie.trivia.length > 0) {
    const tSec = document.createElement('section');
    tSec.style.marginTop = 'var(--space-xl)';
    tSec.innerHTML = `
      <div class="section-header"><h2 class="section-title">Behind the Scenes Trivia</h2></div>
      <ul style="padding-left: 20px; color: var(--text-secondary); font-size: 0.9rem; line-height: 1.6;">
        ${movie.trivia.map(t => `<li style="margin-bottom: 8px;">${t}</li>`).join('')}
      </ul>
    `;
    dynamicSections.appendChild(tSec);
  }

  // Awards (Conditional)
  if (movie.awards && movie.awards.length > 0) {
    const aSec = document.createElement('section');
    aSec.style.marginTop = 'var(--space-xl)';
    aSec.innerHTML = `
      <div class="section-header"><h2 class="section-title">Awards & Accolades</h2></div>
      <div style="display: flex; gap: var(--space-xs); flex-wrap: wrap;">
        ${movie.awards.map(a => `
          <div class="badge-pill badge-gold" style="padding: 6px 12px;">
            🏆 <strong>${a.ceremony} (${a.year})</strong>: ${a.category}
          </div>
        `).join('')}
      </div>
    `;
    dynamicSections.appendChild(aSec);
  }

  // Interviews (Conditional)
  const intEl = createInterviewsList(movie.interviews);
  if (intEl) dynamicSections.appendChild(intEl);

  // Discussion Board
  const forumEl = createDiscussionBoard(movie);
  container.querySelector('#discussion-placeholder').appendChild(forumEl);

  // Event Handlers
  const rateBtn = container.querySelector('#rate-movie-btn');
  rateBtn.addEventListener('click', () => {
    createRatingSheet(movie, (res) => {
      container.querySelector('#display-rating').textContent = res.averageRating;
      container.querySelector('#display-count').textContent = res.ratingCount;
      container.querySelector('#rate-btn-text').textContent = `Your: ${res.userRating}★`;
    });
  });

  const bookmarkBtn = container.querySelector('#bookmark-movie-btn');
  bookmarkBtn.addEventListener('click', () => {
    let list = [];
    try {
      list = JSON.parse(localStorage.getItem('cmdb_watchlist') || '[]');
    } catch (e) {
      list = [];
    }
    const idx = list.indexOf(movie.id);
    if (idx > -1) {
      list.splice(idx, 1);
      Toast.show(`Removed ${movie.title} from your Watchlist`);
    } else {
      list.push(movie.id);
      Toast.show(`Added ${movie.title} to your Watchlist!`, 'success');
    }
    localStorage.setItem('cmdb_watchlist', JSON.stringify(list));
  });

  const shareStoryBtn = container.querySelector('#share-story-btn');
  if (shareStoryBtn) {
    shareStoryBtn.addEventListener('click', () => {
      openStoryCardModal(movie);
    });
  }

  return container;
}
